import React from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import PositionedFieldsEditor from "./PositionedFieldsEditor";
import { getMandatoryFields } from "../../../hooks/usePdfFieldsRules";
import OrderedFieldsSection from "./OrderedFieldsSection";

function PDFFieldsSection({
  title,
  fields,
  onReorder,
  onRemove,
  onAdd,
  availableFields,
  maxFields = Infinity,
  pdfType,
  enablePreview = true,
  isItemLabel = false,
  positionedFields,
  onPositionedAdd,
  onPositionedRemove,
}) {
  const mandatoryFields = getMandatoryFields(pdfType);

  const hasExceeded = (fields || []).length > maxFields;
  const warningMessage = isItemLabel 
    ? `This PDF allows for a maximum of ${maxFields} optional fields. The fields highlighted in yellow will be excluded.`
    : `This PDF allows for a maximum of ${maxFields} columns. The fields highlighted in yellow will be excluded.`;

  if (isItemLabel) {
    return (
      <Paper
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: 600,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography>{title}</Typography>
        </Box>
        {hasExceeded && (
          <Box p={2} sx={{ backgroundColor: "#ffebee" }}>
            <Typography variant="body2" sx={{ color: "error.main", fontWeight: 500 }}>
              {warningMessage}
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
            Base Template:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            • Logo • QR Code • Customer Name • Shipment ID • Description • Quantity •Expiration • Received Date • Item ID & SKU Number
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.primary", ml: 1 }}>
            Additional Fields:
          </Typography>
          <PositionedFieldsEditor
            positionedFields={positionedFields}
            availableFields={availableFields}
            onAdd={onPositionedAdd}
            onRemove={onPositionedRemove}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <OrderedFieldsSection
      title={title}
      fields={fields}
      onReorder={onReorder}
      onRemove={onRemove}
      onAdd={onAdd}
      availableFields={availableFields}
      maxFields={maxFields}
      mandatoryFields={mandatoryFields}
      addButtonLabel="Add Another Field to PDF"
      emptyStateText="No fields selected"
      enablePreview={enablePreview && !isItemLabel}
    >
      {hasExceeded && (
        <Box p={2} sx={{ backgroundColor: "#ffebee" }}>
          <Typography variant="body2" sx={{ color: "error.main", fontWeight: 500 }}>
            {warningMessage}
          </Typography>
        </Box>
      )}
    </OrderedFieldsSection>
  );
}

export default PDFFieldsSection;
